// DEPENDENCIES
import isBefore from 'date-fns/isBefore';
import { toZonedTime } from 'date-fns-tz';
import HTMLReactParser, { domToReact } from 'html-react-parser';
import _ from 'lodash';
import queryString from 'query-string';
import sanitizeHtml from 'sanitize-html';

// REDUX
import { store } from 'store.js';
import { alertActions, modalActions, sessionActions } from 'actions.js';

// DB
import { db } from 'db.js';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { FETCH, DATE, LOGOUT } from 'defaults.js';
import {
  CHART_COLORS,
  DEBUG,
  ENVIRONMENTS,
  FILE_CHUNK_SIZE,
  MODAL_PRIORITY,
  PATH,
  PRIVILEGES,
  USER_ROLES,
  VIEW_STATUSES
} from 'globals.js';
import { INTERFACE } from 'pathnames.js';


// Utility

export const bugLog = (feedback, debug = DEBUG, name, value) => {
  if (name) feedback = `[${name}] ${feedback}`;
  if (value !== undefined) feedback = `${feedback}: ${value}`;
  // eslint-disable-next-line no-console
  if (debug) console.log(feedback);
}

export const doCallback = (callback, val, ...rest) => {
  if (_.isFunction(callback)) return callback(val, ...rest);
  return val;
}

export const debounce = (func, timeout = 600) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

export const randomID = () => Math.random().toString(36).substring(2, 15).toUpperCase();

export const controlName = (name, ...parents) => [...parents, name].filter(p => p !== undefined && p !== null && p !== '').join('.')

export const arrayToCSV = (array, separator = ',') => array.filter(el => el !== '').join(separator);

export const sleep = milliseconds => {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
};

export const isTrue = v => ['true', true].includes(v);

export const ensureBoolean = value => {
  if (_.isString(value)) {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  return value;
}


// User Role/Privileges

export const checkUser = (key, userProperty, global) => {
  if (!key) return true;

  let found = false;
  const checkHaystack = key => {
    const needle = global[key];
    const haystack = store.getState().user[userProperty];
    if (!needle) return;
    if (Array.isArray(haystack)) {
      if (haystack.includes(needle)) found = true;
    } else if (needle === haystack) {
     found = true;
    }
  }

  if (Array.isArray(key)) {
    key.forEach(e => checkHaystack(e))
  } else {
    checkHaystack(key)
  }
  return found;
}

export const checkRole = role => {
  return checkUser(role, 'role_key', _.reduce(USER_ROLES, (r, { key }, k) => { r[k] = key; return r }, {}));
}

export const checkPrivilege = privilege => {
  return checkUser(privilege, 'privileges', PRIVILEGES);
}


// API

export const getEnv = key => {
  let val = ENVIRONMENTS.dev[key];
  Object.keys(ENVIRONMENTS).forEach(env => {
    if (window.location.hostname === ENVIRONMENTS[env].hostname) val = ENVIRONMENTS[env][key];
  })
  return val;
}

export const getURL = () => `${getEnv('url')}`;

export const getKey = () => getEnv('key');

export const getEndpoint = (endpoint, prop) => typeof endpoint === 'function' ? !prop ? undefined : endpoint(prop) : endpoint;

export const stringifyBody = (values, stringify, blacklist = []) => {
  if (Array.isArray(values)) {
    return FETCH.useCSV ? values.join(',') : values;
  } else if (typeof values === 'object') {
    _.keys(values).forEach(key => {
      if (blacklist.includes(key)) delete values[key];
      else values[key] = stringifyBody(values[key]);
    })
    return stringify ? JSON.stringify(values) : values;
  } else {
    return values;
  }
}

export const apiFetch = ({
  method = 'GET',
  path = PATH,
  endpoint,
  headers = {},
  body,
  params,
  useQueryString = method === 'GET',
  queryParams = useQueryString ? params : undefined,
  onFetch,
  onResponse,
  onSuccess,
  onError,
  onAbort,
  onComplete,
  loadingMessage,
  successMessage,
  errorMessage,
  abortMessage = 'Request aborted.',
  messageFunctions = modalFunctions,
  allowAbort,
  abortController = new AbortController(),
  debugOnly,
  debug = debugOnly,
}) => {

  let fetchLog = feedback => bugLog(feedback, debug);

  if (!endpoint) {
    doCallback(onError);
    // eslint-disable-next-line no-console
    return console.error('No endpoint specified!');
  }

  const { token } = store.getState();
  let url = 'https:/' + makePath(getURL(), path, endpoint);

  let config = {};
  config.method = method;
  config.headers = Object.assign({
    'Content-Type': 'application/json',
    'x-api-key': getKey()
  }, headers);
  config.credentials = 'include';
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  config.signal = abortController.signal;
  if (body) config.body = body;
  if (queryParams) url += '?' + queryString.stringify(queryParams);
  if (!useQueryString) config.body = stringifyBody(params, true, FETCH.blacklist);

  if (params) fetchLog(params);

  fetchLog(url);
  fetchLog(config);

  if (debugOnly) return;

  const clearLoadingMessage = loadingMessage ? messageFunctions.loading(loadingMessage, allowAbort && abortController) : undefined;
  doCallback(onFetch);

  fetch(url, config)
    .then(async (response) => {

      fetchLog(response);

      doCallback(clearLoadingMessage);
      doCallback(onResponse, response);

      let errorObject;
      
      if (!response.ok) {
        try {
          // If error is a custom object with message(s)
          errorObject = {
            error: await response.json(),
            status: response.status
          };
        } catch (err) {
          errorObject = {
            error: Error(response),
            status: response.status
          }
        }
      }

      if (errorObject) {
        throw errorObject;
      } else {
        return response;
      }
    })
    .then(response => response.json())
    .then((data) => {

      fetchLog(data);

      if (data.status === false || data?.success === false) {
        if(errorMessage !== false) messageFunctions.error(data.ERROR || data.error || data.message || errorMessage);
        doCallback(onError, data);
      } else {
        messageFunctions.success(successMessage);
        doCallback(onSuccess, data);
      }
    })
    .catch(({ error = {}, name, status }) => {

      if (status === 401) {
        logOut({
          alerts: [
            {
              variant: 'danger',
              message: 'Session expired.'
            }
          ]
        }, false)
        return;
      }

      let message;
      const isAbort = name === 'AbortError';
      
      if (!isAbort) {
        message = _.isFunction(errorMessage) ? errorMessage(error, status) : errorMessage;
      } else if (allowAbort) {
        message = abortMessage;
      }

      if (message) messageFunctions.error(message);

      doCallback(clearLoadingMessage);
      doCallback(onError, error, status);
      if (isAbort) doCallback(onAbort);
    })
    .finally(() => {
      doCallback(onComplete)
    })
}

export const asyncFetch = async ({
  method = 'GET',
  path = PATH,
  endpoint,
  params,
  errorMessage,
  debug,
  debugOnly
}) => {
  const fetchLog = feedback => bugLog(feedback, debug);

  let url = 'https:/' + makePath(getURL(), path, endpoint);
  if (method === 'GET') url += '?' + queryString.stringify(params);
  fetchLog(url);

  try {
    const { token } = store.getState();

    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        'x-api-key': getKey()
      }
    };
    if (method !== 'GET' && !_.isEmpty(params)) config.body = JSON.stringify(params);
    fetchLog(config);

    if (debugOnly) return;

    const response = await fetch(url, config);
    const result = await response.json();
    fetchLog(result);

    if (response.status !== 200) {
      throw new Error(result.message || errorMessage || 'An error occurred.');
    }

    return result;
  } catch (err) {
    throw new Error(err);
  }
};

export const documentFetch = async ({
  path = PATH,
  endpoint,
  accept,
  contentType,
  mimeType = '',
  params,
  onFetch,
  onResponse,
  onSuccess,
  onError,
  onComplete,
  loadingMessage,
  successMessage,
  errorMessage,
  messageFunctions = modalFunctions,
  debugOnly,
  debug = debugOnly
}) => {
  const fetchLog = feedback => bugLog(feedback, debug);

  if (!endpoint) {
    doCallback(onError);
    // eslint-disable-next-line no-console
    return console.error('No endpoint specified!');
  }

  let url = 'https:/' + makePath(getURL(), path, endpoint);
  if (params) {
    url += '?' + queryString.stringify(params);
    fetchLog(params);
  }
  fetchLog(url);

  const config = {
    method: 'GET',
    headers: {
      Accept: accept,
      'Content-Type': contentType,
      'x-api-key': getKey()
    },
    credentials: 'include'
  };
  const { token } = store.getState();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  fetchLog(config);

  if (debugOnly) return;

  const clearLoadingMessage = loadingMessage ? messageFunctions.loading(loadingMessage) : undefined;
  doCallback(onFetch);

  try {
    const response = await fetch(url, config);

    fetchLog(response);

    doCallback(onResponse, response);

    if (!response.ok) throw Error(response);

    const buffer = await response.arrayBuffer();

    const blob = new Blob([buffer], { type: mimeType.mime || mimeType });
    const documentUrl = window.URL.createObjectURL(blob);
    fetchLog(documentUrl);

    doCallback(clearLoadingMessage);
    messageFunctions.success(successMessage);
    doCallback(onSuccess, { url: documentUrl, blob, mimeType });
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    messageFunctions.error(errorMessage);
    doCallback(clearLoadingMessage);
    doCallback(onError);
  } finally {
    doCallback(onComplete);
  }
};

export const documentChunkFetch = async ({
  path = PATH,
  endpoint,
  accept,
  contentType,
  mimeType = '',
  params = {},
  onFetch,
  onSuccess,
  onError,
  onComplete,
  loadingMessage,
  successMessage,
  errorMessage,
  messageFunctions = modalFunctions,
  debugOnly,
  debug = debugOnly
}) => {
  const fetchLog = feedback => bugLog(feedback, debug);

  if (!endpoint) {
    doCallback(onError);
    // eslint-disable-next-line no-console
    return console.error('No endpoint specified!');
  }

  let url = 'https:/' + makePath(getURL(), path, endpoint);

  const config = {
    method: 'GET',
    headers: {
      'x-api-key': getKey()
    },
    credentials: 'include'
  };
  if (accept) config.headers.Accept = accept;
  if (contentType) config.headers['Content-Type'] = contentType;
  const { token } = store.getState();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  fetchLog(config);

  if (debugOnly) return;

  const clearLoadingMessage = loadingMessage ? messageFunctions.loading(loadingMessage) : undefined;
  doCallback(onFetch);

  try {
    // Initialize
    const initUrl = url + '?' + queryString.stringify({ ...params, viewAction: 'init' });

    const initResponse = await fetch(initUrl, config);

    const initData = await initResponse.json();

    if (!initResponse?.ok || !initData.status) {
      throw new Error(initData?.errors?.join(', ') || initData?.message || 'Failed to initialize file download.');
    }

    const { fileSize } = initData;

    // Function to invoke for each document chunk
    const fetchDocumentChunk = async fetchUrl => {
      const response = await fetch(fetchUrl, config);

      fetchLog(response);
      
      if (!response.ok) {
        const fetchData = await response.json();
        throw new Error(fetchData?.errors?.join(', ') || fetchData?.message || 'Failed to download file.');
      }

      const binary = await response.arrayBuffer();
      return binary;
    }

    let loadedSize = 0;
    const chunkRequests = [];

    params.viewAction = 'view';

    // Interate and build document from chunks
    while (loadedSize < fileSize) {
      const startByte = loadedSize;
      const endByte = Math.min(startByte + FILE_CHUNK_SIZE - 1, fileSize - 1);

      params.startByte = startByte;
      params.endByte = endByte;
      fetchLog(params);

      const fetchUrl = url + '?' + queryString.stringify(params);
      fetchLog(fetchUrl);

      const chunkRequest = fetchDocumentChunk(fetchUrl);
      chunkRequests.push(chunkRequest);

      loadedSize += (endByte - startByte) + 1;
    }

    const chunks = await Promise.allSettled(chunkRequests);

    const chunkArray = [];

    // Refetch any failed chunk requests
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      if (chunk.status === 'rejected') {
        const refetchStartByte = i * FILE_CHUNK_SIZE;
        const refetchUrl = url + '?' + queryString.stringify({
          ...params,
          startByte: refetchStartByte,
          endByte: Math.min(refetchStartByte + FILE_CHUNK_SIZE - 1, fileSize - 1)
        });
        // eslint-disable-next-line no-await-in-loop
        const refetchedChunk = await fetchDocumentChunk(refetchUrl);
        chunkArray.push(refetchedChunk);
      } else {
        chunkArray.push(chunk.value);
      }
    }

    const blob = new Blob(chunkArray, { type: mimeType });
    const documentUrl = window.URL.createObjectURL(blob);
    fetchLog(documentUrl);

    doCallback(clearLoadingMessage);
    messageFunctions.success(successMessage);
    doCallback(onSuccess, { url: documentUrl, blob, mimeType });
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    messageFunctions.error(errorMessage || error.message || 'Failed to download file.');
    doCallback(clearLoadingMessage);
    doCallback(onError);
  } finally {
    doCallback(onComplete);
  }
};


// FILE

export const convertFileToBlob = async file => {
  try {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(new Blob([reader.result], { type: file.type }));
      };
      reader.onerror = () => {
        reject(new Error('Failed to read the file as a blob.'));
      };
      reader.readAsArrayBuffer(file);
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return null; 
  }
}

export const fileBlob = (file, blobType = 'application/pdf') => URL.createObjectURL(new Blob([file], {type: blobType !== false ? blobType : ''}));

// TODO: Do we really need all these parameters?
export const fileDownload = (url, fileName = 'download', extension, useDate, preserveCase) => {
  if (!url) return;
  let downloadName = preserveCase ? fileName : _.snakeCase(fileName);
  if (useDate) downloadName += `_${formatDate(new Date(), 4, false, '_')}`;
  if (extension) downloadName += `.${extension}`;
  let a = document.createElement('a');
  a.href = url;
  a.download = downloadName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export const shouldOpenFile = mimeType => {
  // Either a PDF or image
  return mimeType === 'application/pdf' || mimeType.startsWith('image/');
}

export const viewFile = async ({ url, blob, fileName = 'download', mimeType: fileType }, ...download) => {
  if (blob?.size === 0) {
    modalFunctions.error('Unable to download file.');
    return false;
  }

  const mimeType = fileType || getFileType(fileName);
  if (blob && shouldOpenFile(mimeType)) {
    try {
      await db.files.put({
        fileName,
        blob,
        mimeType
      });
      window.open(`/${INTERFACE.viewFile}?fileName=${fileName}`, '_blank');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err?.message);
      fileDownload(url, fileName, ...download);
    }
  } else {
    fileDownload(url, fileName, ...download);
  }
}

export const filePrint = url => {
  let iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  iframe.onload = () => {
    setTimeout(() => {
      iframe.focus();
      iframe.contentWindow.print();
    }, 1)
  }
  document.body.appendChild(iframe);
};

export function getFileType(filepath = '') {
  const fileExt = filepath.split('.').pop().toLowerCase();
  switch(fileExt) {
    case 'pdf':
      return 'application/pdf';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'csv':
      return 'text/csv';
    case 'txt':
      return 'text/plain';
    default:
      return '';
  }
}

// Session

export const timeExpired = timeStamp => {
  if (!timeStamp) return false;

  const nowUtc = toZonedTime(new Date(), 'UTC');
  const expUtc = toZonedTime(new Date(timeStamp), 'UTC');

  return isBefore(expUtc, nowUtc);
};

export const loggedIn = (token, tokenExp) => token && !timeExpired(tokenExp);

export const logOut = async (payload, useFetch = LOGOUT.useFetch) => {

  const LOGOUT_ALERT = {
    type: 'loading',
    message: 'Logging out',
    priority: MODAL_PRIORITY.override
  }

  const dispatchLogout = () => store.dispatch(sessionActions.logout(payload))

  if (useFetch) {

    const args = {
      method: 'GET',
      endpoint: ENDPOINTS.session.logout,
      onResponse: modalFunctions.add(LOGOUT_ALERT),
      onSuccess: async () => {
        await db.delete();
        dispatchLogout();
      },
      errorMessage: 'Unable to sign out.'
    }

    apiFetch(args);
  } else {
    await db.delete();
    dispatchLogout();
  }
}


// Router

export const makePath = (...parts) => _.replace(`/${_.filter(parts, p => !!p).map(p => _.trim(p, '/')).join('/')}/`, '//', '/');

export const getPathByStatus = (rawStatus, id) => {
  const status = _.lowerCase(rawStatus).replace(/\s/g, '');
  
  const view = _.values(VIEW_STATUSES).find(s => s.statuses.includes(status))?.view || 'sales';

  return makePath(view, 'view', id);
}

// Alerts / Modals

const addAlert = alert => {
  if (!alert || !alert.message) return;
  alert.ID = randomID();
  store.dispatch(alertActions.add(alert));
  return () => store.dispatch(alertActions.remove(alert.ID));
}

export const alertFunctions = {
  add:     addAlert,
  remove:  alertID => store.dispatch(alertActions.remove(alertID)),
  clear:   alerts  => store.dispatch(alertActions.clear(alerts)),
  message: message => addAlert({ message, variant: 'secondary' }),
  success: message => addAlert({ message, variant: 'success' }),
  warning: message => addAlert({ message, variant: 'warning' }),
  danger:  message => addAlert({ message, variant: 'danger' }),
  info:    message => addAlert({ message, variant: 'info' }),
  error:   message => addAlert({ message, variant: 'danger' }),
  loading: message => addAlert({ message, type: 'loading' })
}

const addModal = modal => {
  if (!modal) return;
  modal.ID = randomID();
  if (modal.priority === undefined) modal.priority = MODAL_PRIORITY.default;
  store.dispatch(modalActions.add(modal));
  return () => store.dispatch(modalActions.remove(modal.ID));
}

export const modalFunctions = {
  add:        addModal,
  remove:     modalID => store.dispatch(modalActions.remove(modalID)),
  increment:  ()      => store.dispatch(modalActions.increment()),
  clear:      ()      => store.dispatch(modalActions.clear()),
  message:    message => !message ? null : addModal({ message, type: 'alert', variant: 'secondary' }),
  success:    message => !message ? null : addModal({ message, type: 'alert', variant: 'success' }),
  warning:    message => !message ? null : addModal({ message, type: 'alert', variant: 'warning' }),
  danger:     message => !message ? null : addModal({ message, type: 'alert', variant: 'danger' }),
  info:       message => !message ? null : addModal({ message, type: 'alert', variant: 'info' }),
  error:      message => !message ? null : addModal({ message, type: 'error' }),
  loading:    (message, controller) => !message ? null : addModal({ message, type: 'loading', priority: MODAL_PRIORITY.low, controller })
}


// Numbers / Date / Time

export const precedingZero = (num) => {
  let numString = num.toString();
  if (numString.length === 1)
    numString = '0' + numString;
  return numString;
}

export const toNumber = (num) => {
  return isNaN(num) ? 0 : num;
}

export const formatPlural = (value = 0, unit, usePrecedingZero = DATE.usePrecedingZero, abbreviate) => {
  const float = parseFloat(value);
  if (isNaN(float)) return '';
  return abbreviate ? `${float}${unit.charAt(0)}` : `${float} ${unit}${parseFloat(float) !== 1 ? 's' : ''}`;
};

export const compare = (value, operator, operand) => {
  switch (operator) {
    case '===':
      return value === operand;
    case '>':
      return value > operand;
    case '<':
      return value < operand;
    case '>=':
      return value >= operand;
    case '<=':
      return value <= operand;
    default:
      return undefined;
  }
}

export const checkDate = (value, operator, operand) => {
  value = getDay(value).getTime();
  operand = getDay(operand).getTime();

  if (typeof operator === 'undefined') // If no operator is defined, just check that value is a valid date.
    return !isNaN(value);

  if (isNaN(value) && isNaN(operand)) // Else, first check that dates are valid.
    return undefined;

  return compare(value, operator, operand); // Return comparison;
};

export const checkTime = value => {
  let date = getTimeDate(value);

  return checkDate(date);
}

export const isDate = value => {
  return value && new Date(value) instanceof Date && !isNaN(new Date(value));
}

export const getDay = (date) => {
  date = date instanceof Date ? date : new Date(date);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export const getTimeDate = (timeString) => {
  return timeString instanceof Date ? timeString : new Date(`January 1 1970 ${timeString}`);
}

export const getHours = (timeString, usePrecedingZero = false) => {
  const time = getTimeDate(timeString);

  const convertToTwelveHours = (hours) => {
    const h = hours % 12;
    return h === 0 ? 12 : h;
  }
  const hours = convertToTwelveHours(time.getHours());

  return usePrecedingZero ? precedingZero(hours) : hours;
}

export const getMinutes = (timeString, usePrecedingZero = true) => {
  const time = getTimeDate(timeString);

  return usePrecedingZero ? precedingZero(time.getMinutes()) : time.getMinutes();
}

export const getMeridian = (timeString) => {
  const time = getTimeDate(timeString);

  return time.getHours() > 11 ? 'PM' : 'AM';
}

export const getMidnight = (date) => {
  date = date instanceof Date ? date : new Date(date);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export const timeToObject = (timeString) => {
  if (!timeString) return;
  return {
    hours: getHours(timeString, false),
    minutes: getMinutes(timeString, false),
    meridian: getMeridian(timeString)
  }
}

export const timeToString = (timeObject, usePrecedingZero) => {
  if (!timeObject) return;
  const hours = usePrecedingZero ? precedingZero(timeObject.hours) : timeObject.hours;
  const minutes = precedingZero(timeObject.minutes);
  const meridian = timeObject.meridian;

  return `${hours}:${minutes} ${meridian}`;
}

export const safeDate = date => {
  const dateObject = new Date(date);
  return isNaN(dateObject.getTime()) ? '' : dateObject;
}

export const safeTime = (time, date = '1/1/1') => {
  if (!time) return;
  if (time instanceof Date) return time;
  return safeDate([date, time].join(' '));
}

export const compareDate = (value, operator, operand) => {
  value = getMidnight(value).getTime();
  operand = getMidnight(operand).getTime();

  if (typeof operator === 'undefined') // If no operator is defined, just check that value is a valid date.
    return !isNaN(value);

  if (isNaN(value) && isNaN(operand)) // Else, first check that dates are valid.
    throw Error;

  return compare(value, operator, operand); // Return comparison;
}

export const formatDate = (value, yearLength = DATE.yearLength, usePrecedingZero = DATE.usePrecedingZero, separator = '/') => {
  const date = value instanceof Date ? value : new Date(value);
  let month = date.getMonth() + 1;
  if (usePrecedingZero) month = precedingZero(month);
  let day = date.getDate();
  if (usePrecedingZero) day = precedingZero(day);
  let year = date.getFullYear().toString().substring((4 - yearLength), 4);
  return value && `${month}${separator}${day}${separator}${year}`;
}

export const formatTime = (value, usePrecedingZero) => {
  const time = safeDate(value);
  if (!time) return '';
  return timeToString(timeToObject(time), usePrecedingZero);
}

export const formatDateTime = (value, yearLength = 4, usePrecedingZero = false, separator = '/') => {
  if (!value) return value;
  return `${formatDate(value, yearLength, usePrecedingZero, separator)} ${formatTime(safeDate(value), usePrecedingZero)}`
}

export const formatDollar = (amount, settings = {}) => {
  if (amount === undefined || amount === null || amount === '') return;
  amount = parseFloat(String(amount).replace('$', ''));
  if (isNaN(amount)) return 'NaN';
  const dollarFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    ...settings
  })
  return dollarFormat.format(parseFloat(amount)).replace(/^(\D+)/, '$1 ')
}

export const formatMiles = miles => {
  if (miles === undefined || miles === null || miles === '') return;
  if (isNaN(miles)) return 'NaN';
  const milesFormat = new Intl.NumberFormat().format(miles)
  return milesFormat
}

export const formateName =(str,splt="  ") =>{
  const v = str.split(splt)
  return v
}

export const dollarsToNumber = value =>  +value.replace(/\$|,/g, '');

export const formatFunctions = {
  dollar: formatDollar,
  date: formatDate,
  dateTime: formatDateTime,
  time: formatTime,
  miles: formatMiles
}

// Charts

export const buildChartColors = (numDatum = 0) => {
  const colors = _.values(CHART_COLORS);
  while (numDatum > colors.length) {
    colors.push(...colors);
  }
  return colors;
};

// HTML Parsing

export const parseLink = (el, attributeName, pathname) => {
  const replace = domNode => {
    const { name, attribs } = domNode;

    if (name !== 'a') return;

    if (Object.keys(attribs).includes(attributeName)) {
      const attributeValue = domNode.attribs[attributeName];
      const children = domToReact(domNode.children, replace);
      return <a href={makePath(pathname, attributeValue)}>{_.trim(children)}</a>;
    }
  };

  const sanitized = sanitizeHtml(el, {
    allowedAttributes: false,
    allowedSchemes: ['https'],
    disallowedTagsMode: 'escape'
  });

  return <>{HTMLReactParser(sanitized, {replace})}</>;
};

